/* eslint-disable no-param-reassign */
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit'
import { get, uniq } from 'lodash'
import getPassengerTypeApiKeys from 'utilities/getPassengerTypeApiKeys'
import generateRandomRph from '../utilities/generateRandomRph'
import getPassengerCountForAddOns from '../utilities/getPassengerCountForAddOns'

const handleFetch = createAction('config/fetch')
export const handleReceive = createAction('config/receive')
export const handleError = createAction('config/error')
export const setActiveSessionCount = createAction('config/setSessionCount')

export const setAccommodationsAreDisabled = createAction(
  'config/setAccommodationsAreActive',
)

export const setFerryAccommodations = createAction(
  'config/setFerryAccommodations',
)

export const setFerryAccommodationsFilters = createAction(
  'config/setFerryAccommodationsFilters',
)

export const setFerryAttractions = createAction('config/setFerryAttractions')

const initialState = {
  loading: false,
  data: null,
  error: null,
  sessionCount: null,
  accommodationsAreDisabled: false,
  ferryAccommodations: [],
  ferryAttractions: [],
  accommodationFilters: {},
}

export const configReducer = createReducer(initialState, builder => {
  builder
    .addCase(handleFetch, state => {
      state.loading = true
    })
    .addCase(handleReceive, (state, action) => {
      state.loading = false
      state.data = action.payload.config
      state.sessionCount = action.payload.sessionCount
    })
    .addCase(handleError, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
    .addCase(setActiveSessionCount, (state, action) => {
      state.sessionCount = action.payload
    })
    .addCase(setAccommodationsAreDisabled, (state, action) => {
      state.accommodationsAreDisabled = action.payload
    })
    .addCase(setFerryAccommodations, (state, action) => {
      state.ferryAccommodations = action.payload
    })
    .addCase(setFerryAccommodationsFilters, (state, action) => {
      state.accommodationFilters = action.payload
    })
    .addCase(setFerryAttractions, (state, action) => {
      state.ferryAttractions = action.payload
    })
})

const selectStateConfig = state => state.configuration

export const selectConfigState = createSelector(
  selectStateConfig,
  configuration => configuration,
)

export const selectConfigData = createSelector(
  selectStateConfig,
  config => config?.data || {},
)

export const selectConfigError = createSelector(
  selectConfigState,
  state => state.error,
)

export const selectFerryLabels = createSelector(
  selectConfigData,
  data => data?.ferry?.labels || {},
)

export const selectFerryRouteOptions = createSelector(
  selectConfigData,
  config => Object.values(config?.ferry?.options?.routes || {}).flat(),
)

export const selectDefaultPassengerTypes = createSelector(
  selectConfigData,
  config =>
    config?.ferry?.options?.routes?.rf?.[0]?.passengerTypes?.map(
      passengerType => ({
        ...passengerType,
        apiKey: getPassengerTypeApiKeys(passengerType.type),
      }),
    ) || null,
)

export const selectAllVehicleOptions = createSelector(
  selectConfigData,
  config => {
    return config?.ferry?.options?.vehicleResourceCodesExpanded || []
  },
)

export const selectTrailerOptions = createSelector(
  selectConfigData,
  config => config?.ferry?.options?.trailerResourceCodes || [],
)

export const selectPassengerResourcesByRoute = createSelector(
  [selectFerryRouteOptions, (state, code) => code],
  (routes, routeCode) =>
    routes?.find(route => route.code === routeCode)?.passengerTypes || [],
)

export const selectFerryConfigOptions = createSelector(
  selectConfigData,
  config => config?.ferry?.options || {},
)

export const selectCargoResourceCodes = createSelector(
  selectFerryConfigOptions,
  options => options?.cargoResourceCodes || [],
)

export const selectStaticCrossingParams = createSelector(
  selectFerryConfigOptions,
  options => {
    return {
      productCode: options?.defaultProductCode || '',
      currency: options?.routes?.[0]?.displayCurrency || 'GBP',
    }
  },
)

export const selectWalkOnPassengerCode = createSelector(
  selectFerryConfigOptions,
  options => options?.walkOnPassengerResourceCode,
)

export const selectPassengerResourceCode = createSelector(
  selectFerryConfigOptions,
  options => options?.passengerResourceCode,
)

export const selectRouteDataByCode = createSelector(
  [selectFerryRouteOptions, (state, code) => code],
  (routes, code) => routes?.find(route => route.code === code),
)

export const selectDefaultProductCode = createSelector(
  selectFerryConfigOptions,
  config => config?.defaultProductCode,
)

// nonOverridableProductCodes
export const selectNonOverRideProductCodes = createSelector(
  selectFerryConfigOptions,
  config => config?.nonOverridableProductCodes || [],
)

export const selectShipOptions = createSelector(
  selectFerryConfigOptions,
  options => options.ships || [],
)

export const selectAccommodationsAreDisabled = createSelector(
  selectConfigState,
  config => {
    return (
      config.accommodationsAreDisabled &&
      config.accommodationsAreDisabled !== 'false'
    )
  },
)

export const selectFerryAccommodations = createSelector(
  selectConfigState,
  state => state.ferryAccommodations,
)

export const selectFerryAccommodationsFilters = createSelector(
  selectConfigState,
  state => state.accommodationFilters,
)

export const selectFerryAttractions = createSelector(
  selectConfigState,
  state => state.ferryAttractions,
)

export const formatDepartureRequest = ({
  config = {},
  crossing,
  vehicle,
  customerInfos,
  contactInfos,
  passengers,
}) => {
  const { walkOnPassengerResourceCode } = config

  // ensuring we use the correct product and resource coeds based on the cart create rules.
  const rateData = crossing?.rate

  const selectedAddOns = crossing?.addOns.map(addOn => ({
    resourceCode: addOn.resourceCode,
    ticketType: addOn.ticketType,
    amount: getPassengerCountForAddOns({ passengers, addOn }),
  }))

  const params = {
    passengerResourceCode: vehicle
      ? rateData?.passengerResourceCode
      : walkOnPassengerResourceCode,
    code: crossing?.rate?.code,
    vehicleResourceCode: vehicle ? vehicle?.resourceCode : '',
    supplierCode: crossing?.crossing?.routeCode,
    departureDate: crossing?.crossing?.departureDate,
    departureTime: crossing?.crossing?.departureTime,
    addOns: selectedAddOns || [],
    passengers: customerInfos,
  }

  // The standard (STDW) price of the selected fare, used to show the saving
  // versus the discounted price on the summary and booking cards.
  if (crossing?.rate?.standardPrice) {
    params.standardPrice = crossing.rate.standardPrice
  }

  if (vehicle) {
    params.vehicles = [
      {
        resourceCode: vehicle.resourceCode,
        amount: 1,
        length: null,
        licenseNumber: contactInfos?.[0]?.licensePlate.toUpperCase(),
        trailerLength: vehicle?.isBringingTrailer ? vehicle.trailerLength : '',
        trailerHeight: vehicle?.isBringingTrailer ? vehicle.trailerHeight : '',
      },
    ]
  }

  return params
}

const getChildAge = ({
  passengerType,
  passengerIndex,
  accommodations = [],
  childAge = {},
}) => {
  const isChildPassenger = passengerType === 'child'
  if (!isChildPassenger) return ''
  let childInx = 0
  for (let roomIndex = 0; roomIndex < accommodations.length; roomIndex += 1) {
    const childCount = accommodations[roomIndex]?.guests?.children || 0
    if (childCount) {
      const childIndexInRoom = passengerIndex - childInx
      if (childIndexInRoom >= 0 && childIndexInRoom < childCount) {
        const age =
          accommodations[roomIndex]?.childrenAges?.[childIndexInRoom] ??
          childAge?.[roomIndex]?.[childIndexInRoom] ??
          null
        return age !== null && age !== '' ? `${+age} years` : ''
      }
    }
    childInx += childCount
  }
  return ''
}

export const createCustomerInfos = ({
  userData,
  passengers,
  contactInfos,
  hotelGuests,
  childAge = {},
  accommodations = [],
}) => {
  const customerArray = []
  const departureIds = []
  const returnIds = []
  const hasDeparturePassengers = Object.keys(
    passengers?.departure || {},
  )?.length
  const hasReturnPassengers = Object.keys(passengers?.return || {})?.length
  // we'll get the keys for each trip. then compare departure vs return and push to the correct arrays
  const keys = uniq([
    ...Object.keys(passengers?.departure || {}),
    ...Object.keys(passengers?.return || {}),
    ...Object.keys(hotelGuests || {}),
  ])
  const primaryContact = contactInfos?.[0]

  keys.forEach((passengerType, passengerIndex) => {
    const departureCount = passengers?.departure?.[passengerType]?.count || 0
    const returnCount = passengers?.return?.[passengerType]?.count || 0
    const hotelCount = hotelGuests?.[passengerType]?.count || 0
    const mostPassengers = Math.max(departureCount, returnCount, hotelCount)

    const user = userData && userData?.role === 'customer' ? userData : null
    if (mostPassengers) {
      let index = 0
      while (index < mostPassengers) {
        const isPrimary = !passengerIndex && !index
        const id = generateRandomRph()
        const birthDateText = getChildAge({
          passengerType,
          passengerIndex: index,
          accommodations,
          childAge,
        })
        const data = {
          id,
          status: 11,
          ageCategory: passengerType,
          personName: {
            givenName: isPrimary
              ? primaryContact?.firstName || get(user, 'firstName', '')
              : '',
            middleName: '',
            surName: isPrimary
              ? primaryContact?.lastName || get(user, 'lastName', '')
              : '',
          },
          email: isPrimary
            ? primaryContact?.emailAddress || get(user, 'email', '')
            : '',
          gender: '',
          address: isPrimary ? primaryContact?.address || {} : {},
          licensePlate: isPrimary ? primaryContact?.licensePlate : '',
          viewExtensions: { birthDateText },
          telephone: {
            phoneNumber: isPrimary
              ? primaryContact?.mobilePhoneNumber?.replaceAll(' ', '')
              : '',
          },
        }

        customerArray.push({ customer: data })
        if (index < departureCount && hasDeparturePassengers) {
          departureIds.push({
            customer: {
              id,
            },
          })
        }
        if (index < returnCount && hasReturnPassengers) {
          returnIds.push({
            customer: {
              id,
            },
          })
        }
        index += 1
      }
    }
  })

  return {
    // api requires at least one customer
    customerArray: customerArray?.length
      ? customerArray
      : [
          {
            customer: {
              id: 1,
              status: 11,
              ageCategory: 'adult',
              personName: {
                givenName: '',
                middleName: '',
                surName: '',
              },
              email: '',
              gender: '',
            },
          },
        ],
    departureIds,
    returnIds,
  }
}
